import React, { useState, useEffect, useRef, useCallback } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Search, X, Camera, CheckCircle, Clock, AlertTriangle,
  Package, ZoomIn, ChevronRight, ChevronDown, Loader2, MapPin,
  BarChart3, Hash, ArrowRight, ArrowLeft, Layers, Truck
} from 'lucide-react';
import { format, parseISO, isValid, isPast } from 'date-fns';

const safeDate = (d) => {
  try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; }
  catch { return '-'; }
};

const STATUS_LABELS = {
  completed:'Done', in_progress:'In Progress', pending:'Pending',
  waiting:'Waiting', on_hold:'On Hold', not_assigned:'Not Set'
};
const STATUS_COLORS_DOT = {
  completed:'bg-green-500', in_progress:'bg-amber-500',
  pending:'bg-blue-400', waiting:'bg-gray-300',
  on_hold:'bg-orange-400', not_assigned:'bg-gray-200'
};
const STATUS_HEX = {
  completed:'#10b981', in_progress:'#f59e0b', pending:'#60a5fa',
  waiting:'#d1d5db', on_hold:'#fb923c', not_assigned:'#e5e7eb'
};
const STATUS_BORDER = {
  completed:'#4ade80', in_progress:'#fbbf24', pending:'#93c5fd',
  waiting:'#e5e7eb', on_hold:'#fdba74', not_assigned:'#e5e7eb'
};
const STATUS_BG_PDF = {
  completed:'#f0fdf4', in_progress:'#fffbeb', pending:'#eff6ff',
  waiting:'#f9fafb', on_hold:'#fff7ed', not_assigned:'#f9fafb'
};
const STATUS_COLORS = {
  completed:'bg-green-500', in_progress:'bg-amber-500',
  pending:'bg-blue-400', waiting:'bg-gray-300',
  on_hold:'bg-orange-400', not_assigned:'bg-gray-200'
};
const ST = {
  pending:     { bg:'bg-gray-100',    text:'text-gray-500',    bar:'bg-gray-300',    ring:'border-gray-200'    },
  in_progress: { bg:'bg-blue-100',    text:'text-blue-700',    bar:'bg-blue-500',    ring:'border-blue-300'    },
  completed:   { bg:'bg-emerald-100', text:'text-emerald-700', bar:'bg-emerald-500', ring:'border-emerald-300'  },
  on_hold:     { bg:'bg-amber-100',   text:'text-amber-700',   bar:'bg-amber-500',   ring:'border-amber-300'   },
  waiting:     { bg:'bg-purple-100',  text:'text-purple-700',  bar:'bg-purple-400',  ring:'border-purple-300'  },
  not_assigned:{ bg:'bg-gray-100',    text:'text-gray-400',    bar:'bg-gray-200',    ring:'border-gray-200'    },
};

/* ── Lightbox ─────────────────────────────────────────────────────────────── */
function Lightbox({ images, startIdx, onClose }) {
  const [cur, setCur] = useState(startIdx || 0);
  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCur(c => Math.min(c+1, images.length-1));
      if (e.key === 'ArrowLeft')  setCur(c => Math.max(c-1, 0));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [images.length, onClose]);

  const img = images[cur];
  if (!img) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-black/95 flex flex-col items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors">
        <X className="w-5 h-5" />
      </button>
      <p className="absolute top-5 left-1/2 -translate-x-1/2 text-white/40 text-xs tracking-widest">{cur+1} / {images.length}</p>
      <div className="max-w-4xl w-full flex items-center gap-4" onClick={e => e.stopPropagation()}>
        <button onClick={() => setCur(c => Math.max(c-1,0))} disabled={cur===0} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 disabled:opacity-20 flex items-center justify-center text-white text-xl flex-shrink-0">‹</button>
        <img src={img.image_url} alt={img.caption||'Work photo'} className="flex-1 max-h-[78vh] object-contain rounded-2xl"/>
        <button onClick={() => setCur(c => Math.min(c+1,images.length-1))} disabled={cur===images.length-1} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 disabled:opacity-20 flex items-center justify-center text-white text-xl flex-shrink-0">›</button>
      </div>
      <div className="mt-4 text-center space-y-1">
        {img.caption && <p className="text-white/60 text-sm">{img.caption}</p>}
        {img.uploaded_by_name && <p className="text-white/30 text-xs">📸 {img.uploaded_by_name} · {safeDate(img.created_at)}</p>}
      </div>
    </div>
  );
}

/* ── Proto Code Item Card ─────────────────────────────────────────────────── */
function ProtoItemCard({ item }) {
  const [images, setImages]     = useState({});   // { stageKey: [imgs] }
  const [loadingImg, setLoadingImg] = useState(false);
  const [imgLoaded, setImgLoaded]   = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images, idx }

  // Find current active stage (first non-completed, or last if all done)
  const currentStageIdx = item.stages.findIndex(
    s => s.status !== 'completed' && s.status !== 'not_assigned'
  );
  const lastDoneIdx = item.stages.reduce((acc, s, i) => s.status === 'completed' ? i : acc, -1);
  const activeIdx   = currentStageIdx >= 0 ? currentStageIdx : (lastDoneIdx >= 0 ? lastDoneIdx : 0);
  const currentStage = item.stages[activeIdx];

  const overallPct = item.overallPct || 0;

  // Load images once on mount
  useEffect(() => {
    if (imgLoaded || !item.project_id) return;
    setLoadingImg(true);
    api.get(`/projects/${item.project_id}/images-by-stage`)
      .then(r => {
        const map = {};
        r.data.forEach(g => {
          if (g.item_id !== item.id) return;
          g.stages.forEach(s => {
            const key = `${s.stage_order}_${s.dept_name}`;
            map[key] = s.images || [];
          });
        });
        setImages(map);
        setImgLoaded(true);
      })
      .catch(() => {})
      .finally(() => setLoadingImg(false));
  }, [item.id, item.project_id, imgLoaded]);

  const allImages = Object.values(images).flat();
  const barColor  = overallPct === 100 ? '#10b981' : overallPct > 60 ? '#f59e0b' : '#3b82f6';

  return (
    <>
      {lightbox && (
        <Lightbox images={lightbox.images} startIdx={lightbox.idx} onClose={() => setLightbox(null)} />
      )}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

        {/* ── Top: item info + current location ── */}
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            {/* Left: thumbnail + info */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {/* Item thumbnail */}
              {item.thumbnail_url ? (
                <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 shadow-sm">
                  <img src={item.thumbnail_url} alt={item.item_name} className="w-full h-full object-cover"
                    onError={e => { e.target.parentElement.className='w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0'; e.target.replaceWith(Object.assign(document.createElement('span'),{innerHTML:'📦',className:'text-2xl'})); }} />
                </div>
              ) : (
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Package className="w-6 h-6 text-amber-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {/* Proto code — big & prominent */}
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-mono font-bold px-3 py-1 rounded-xl">
                    <Hash className="w-3.5 h-3.5 opacity-60" />{item.proto_code || '—'}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{item.project_name}</span>
                  <span className="text-xs text-gray-400 font-mono">{item.proj_code}</span>
                </div>
                <p className="font-bold text-gray-900 text-lg mt-1">{item.item_name}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Qty: {item.quantity} {item.unit || 'pcs'}
                  {item.material ? ` · ${item.material}` : ''}
                  {item.client_name ? ` · 👤 ${item.client_name}` : ''}
                </p>
              </div>
            </div>

            {/* Right: progress + current location */}
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {/* Progress */}
              <div className="text-right">
                <span className="text-3xl font-black text-gray-900">{overallPct}%</span>
                <p className="text-xs text-gray-400">{item.doneStages}/{item.totalStages} stages done</p>
              </div>
              {/* Current stage badge */}
              {currentStage && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold"
                  style={{
                    backgroundColor: (currentStage.dept_color || '#f59e0b') + '18',
                    borderColor:     (currentStage.dept_color || '#f59e0b') + '55',
                    color:           currentStage.dept_color || '#b45309'
                  }}
                >
                  <MapPin className="w-3 h-3" />
                  {currentStage.status === 'completed' ? 'All Done' : `At: ${currentStage.dept_name}`}
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div className="h-2.5 rounded-full transition-all duration-700"
                style={{ width: `${overallPct}%`, backgroundColor: barColor }} />
            </div>
          </div>
        </div>

        {/* ── Stage flow ── */}
        <div className="px-5 py-4 overflow-x-auto border-b border-gray-100 bg-gray-50/50">
          {item.stages.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">No production chain configured</p>
          ) : (
            <div className="flex items-center gap-1 min-w-max">
              {item.stages.map((stage, idx) => {
                const isCurrentStage = idx === activeIdx;
                const stageImgs = images[`${stage.stage_order}_${stage.dept_name}`] || [];
                return (
                  <React.Fragment key={stage.id || idx}>
                    <div className={`flex flex-col items-center w-[88px] transition-all ${isCurrentStage ? 'scale-110' : ''}`}>
                      <div className={`w-20 rounded-xl p-2.5 text-center border-2 transition-all overflow-hidden ${
                        isCurrentStage
                          ? 'border-slate-800 bg-slate-800 shadow-lg'
                          : stage.status === 'completed' ? 'border-green-400 bg-green-50'
                          : stage.status === 'in_progress' ? 'border-amber-400 bg-amber-50'
                          : stage.status === 'pending' ? 'border-blue-200 bg-blue-50'
                          : 'border-gray-200 bg-white'
                      }`}>
                        <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${
                          isCurrentStage ? 'bg-white animate-pulse' : (STATUS_COLORS[stage.status] || 'bg-gray-300')
                        }`} />
                        <p className={`text-[10px] font-semibold leading-tight break-words ${isCurrentStage ? 'text-white' : 'text-gray-700'}`}>
                          {stage.dept_name}
                        </p>
                        {stage.task && (
                          <p className={`text-[10px] mt-0.5 font-bold ${isCurrentStage ? 'text-white/80' : 'text-gray-500'}`}>
                            {stage.pct}%
                          </p>
                        )}
                        <span className={`text-[9px] font-semibold mt-0.5 inline-block ${
                          isCurrentStage ? 'text-white/70'
                          : stage.status === 'completed' ? 'text-green-700'
                          : stage.status === 'in_progress' ? 'text-amber-700'
                          : stage.status === 'pending' ? 'text-blue-600'
                          : 'text-gray-400'
                        }`}>{STATUS_LABELS[stage.status]}</span>
                        {stageImgs.length > 0 && (
                          <button
                            onClick={e => { e.stopPropagation(); setLightbox({ images: stageImgs, idx: 0 }); }}
                            className={`mt-1.5 w-full flex items-center justify-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-lg transition-colors ${
                              isCurrentStage ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                            }`}
                          >
                            <Camera className="w-2.5 h-2.5" />{stageImgs.length}
                          </button>
                        )}
                      </div>
                      <p className={`text-[10px] mt-1 ${isCurrentStage ? 'text-slate-600 font-bold' : 'text-gray-400'}`}>
                        #{stage.stage_order}
                      </p>
                    </div>
                    {idx < item.stages.length - 1 && (
                      <div className={`flex-shrink-0 text-base pb-4 ${
                        stage.status === 'completed' ? 'text-green-400' : 'text-gray-200'
                      }`}>→</div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>


      </div>
    </>
  );
}

/* ── Outsource Detail Popup ───────────────────────────────────────────────── */
function OutsourcePopup({ jobs, onClose }) {
  if (!jobs || jobs.length === 0) return null;
  const statusColors = {
    Sent:      'bg-blue-100 text-blue-700',
    Partial:   'bg-purple-100 text-purple-700',
    Received:  'bg-green-100 text-green-700',
    Cancelled: 'bg-gray-100 text-gray-500',
  };
  const safeD = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return '—'; } };
  const today = new Date().toISOString().split('T')[0];
  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center">
              <Truck className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Outsource Details</h3>
              <p className="text-xs text-gray-400">{jobs.length} job{jobs.length > 1 ? 's' : ''} for this stage</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {jobs.map(j => {
            const isOverdue = j.expected_date && j.expected_date < today && !['Received','Cancelled'].includes(j.status);
            const receivedPct = j.qty_sent > 0 ? Math.round((j.qty_received / j.qty_sent) * 100) : 0;
            return (
              <div key={j.id} className={`rounded-xl border p-4 ${isOverdue ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <p className="font-bold text-gray-900">{j.vendor_name}</p>
                    {j.vendor_phone && <p className="text-xs text-gray-400 mt-0.5">📞 {j.vendor_phone}</p>}
                    {j.item_name && <p className="text-xs text-gray-500 mt-0.5">Item: {j.item_name}{j.proto_code ? ` · #${j.proto_code}` : ''}</p>}
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold shrink-0 ${statusColors[j.status] || 'bg-gray-100 text-gray-600'}`}>
                    {j.status}
                  </span>
                </div>
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Qty Received</span>
                    <span className="font-semibold">{j.qty_received} / {j.qty_sent}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${j.status === 'Received' ? 'bg-green-500' : j.qty_received > 0 ? 'bg-purple-500' : 'bg-blue-400'}`}
                      style={{ width: `${receivedPct}%` }} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-xs text-gray-400">Sent</p>
                    <p className="text-xs font-semibold text-gray-700 mt-0.5">{safeD(j.sent_date)}</p>
                  </div>
                  <div className={`rounded-lg p-2 border ${isOverdue ? 'bg-red-100 border-red-200' : 'bg-white border-gray-100'}`}>
                    <p className={`text-xs ${isOverdue ? 'text-red-500' : 'text-gray-400'}`}>Expected</p>
                    <p className={`text-xs font-semibold mt-0.5 ${isOverdue ? 'text-red-700' : 'text-gray-700'}`}>{safeD(j.expected_date)}{isOverdue ? ' ⚠' : ''}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-xs text-gray-400">Received</p>
                    <p className="text-xs font-semibold text-gray-700 mt-0.5">{safeD(j.received_date)}</p>
                  </div>
                </div>
                {j.notes && <p className="text-xs text-gray-500 mt-3 bg-white rounded-lg border border-gray-100 px-3 py-2">📝 {j.notes}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* Project progress counted the SAME way the per-item tracker counts it:
   only stages present in each item's CURRENT production chain are counted,
   matched to that item's task. Orphan task_assignments — left over from chain
   edits (removed stages) or deleted items (project_item_id = NULL) — are ignored,
   so the header % matches what you see item-wise. */
function computeChainProgress(items = [], chains = [], tasks = []) {
  let totalStages = 0, completedStages = 0;
  for (const item of items) {
    const itemChains = chains.filter(c => c.project_item_id === item.id);
    const itemTasks  = tasks.filter(t => t.project_item_id === item.id);
    const stageTasks = itemChains.length > 0
      ? itemChains.map(chain => itemTasks.find(t =>
          Number(t.stage_order) === Number(chain.stage_order) &&
          Number(t.department_id) === Number(chain.department_id)))
      : itemTasks;
    for (const task of stageTasks) {
      totalStages++;
      if (task && task.status === 'completed') completedStages++;
    }
  }
  const overallPct = totalStages > 0 ? Math.round((completedStages / totalStages) * 100) : 0;
  return { totalStages, completedStages, overallPct };
}

/* ── Item Stage Tracker (same as client view) ─────────────────────────────── */
function ItemStageTracker({ item, chains, tasks, imageMap, outsourceJobs }) {
  const [open, setOpen]           = useState(true);
  const [activeStageKey, setActiveStageKey] = useState(null);
  const [lightbox, setLightbox]   = useState(null);
  const [outsourcePopup, setOutsourcePopup] = useState(null); // array of jobs for popup

  const itemChains = chains
    .filter(c => c.project_item_id === item.id)
    .sort((a, b) => a.stage_order - b.stage_order);

  const itemTasks = tasks
    .filter(t => t.project_item_id === item.id)
    .sort((a, b) => (a.stage_order||0) - (b.stage_order||0));

  const stages = itemChains.length > 0
    ? itemChains.map(chain => {
        const task = itemTasks.find(t =>
          Number(t.stage_order) === Number(chain.stage_order) &&
          Number(t.department_id) === Number(chain.department_id)
        );
        return { chain, task };
      })
    : itemTasks.map((task, i) => ({
        chain: { stage_order: task.stage_order||i+1, dept_name: task.department_name||task.dept_name, dept_color: task.dept_color, department_id: task.department_id },
        task
      }));

  const completedStages = stages.filter(s => s.task?.status === 'completed').length;
  const totalStages = stages.length;
  const overallPct = totalStages > 0 ? Math.round((completedStages/totalStages)*100) : 0;
  const currentIdx = stages.findIndex(s => s.task?.status === 'in_progress') !== -1
    ? stages.findIndex(s => s.task?.status === 'in_progress')
    : stages.findIndex(s => s.task && s.task.status !== 'completed');

  const handleStageClick = (key, images) => {
    if (images.length === 0) return;
    setActiveStageKey(prev => prev === key ? null : key);
  };

  return (
    <>
      {lightbox && <Lightbox images={lightbox.images} startIdx={lightbox.idx} onClose={() => setLightbox(null)} />}
      {outsourcePopup && <OutsourcePopup jobs={outsourcePopup} onClose={() => setOutsourcePopup(null)} />}}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <button onClick={() => setOpen(o => !o)}
          className="w-full text-left flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors">
          {item.thumbnail_url ? (
            <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
              <img src={item.thumbnail_url} alt={item.item_name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 text-amber-700" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{item.item_name}</span>
              {item.proto_code && <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{item.proto_code}</span>}
              <span className="text-xs text-gray-400">Qty: {item.quantity} {item.unit}</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-[160px]">
                <div className={`h-1.5 rounded-full transition-all ${overallPct===100?'bg-emerald-500':overallPct>0?'bg-amber-500':'bg-gray-300'}`}
                  style={{ width:`${overallPct}%` }} />
              </div>
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{completedStages}/{totalStages} stages</span>
            </div>
          </div>
          <div className="flex-shrink-0 text-gray-400">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </button>

        {open && (
          <div className="border-t border-gray-100 px-4 pb-4 pt-4">
            {stages.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No stages set for this item yet</p>
            ) : (
              <>
                <div className="overflow-x-auto pb-2">
                  <div className="flex items-center gap-1 min-w-max">
                    {stages.map(({ chain, task }, idx) => {
                      const status = task?.status || 'not_assigned';
                      const pct = task && task.quantity_assigned > 0
                        ? Math.round((task.quantity_completed/task.quantity_assigned)*100) : 0;
                      const isCurrentStage = idx === currentIdx;
                      const isCompleted = status === 'completed';
                      const isActive = status === 'in_progress';
                      const imageKey = `${item.id}_${chain.stage_order}_${chain.department_id}`;
                      const stageImages = imageMap[imageKey] || [];
                      const hasImages = stageImages.length > 0;
                      const isExpanded = activeStageKey === imageKey;

                      // Outsource jobs for this item + stage department
                      const stageOutsourceJobs = (outsourceJobs || []).filter(j =>
                        j.project_item_id === item.id && j.department === chain.dept_name
                      );
                      const hasOutsource = stageOutsourceJobs.length > 0;
                      const today = new Date().toISOString().split('T')[0];
                      const outsourceOverdue = hasOutsource && stageOutsourceJobs.some(j =>
                        j.expected_date && j.expected_date < today && !['Received','Cancelled'].includes(j.status)
                      );
                      const outsourceAllDone = hasOutsource && stageOutsourceJobs.every(j => j.status === 'Received');

                      return (
                        <React.Fragment key={idx}>
                          <div className="flex flex-col items-center w-[84px] flex-shrink-0">
                            {/* Stage card */}
                            <button
                              onClick={() => hasImages && handleStageClick(imageKey, stageImages)}
                              className={`w-[76px] rounded-xl p-2 text-center border-2 transition-all focus:outline-none ${hasImages ? 'cursor-pointer' : 'cursor-default'} ${
                                isCurrentStage&&!isCompleted ? 'border-slate-800 bg-slate-800 shadow-lg scale-105'
                                : isCompleted ? 'border-green-400 bg-green-50 hover:shadow-md'
                                : isActive ? 'border-amber-400 bg-amber-50 hover:shadow-md'
                                : status==='pending' ? 'border-blue-200 bg-blue-50'
                                : 'border-gray-200 bg-gray-50'
                              } ${hasImages ? 'ring-2 ring-offset-1 ring-indigo-200' : ''} ${isExpanded ? 'ring-indigo-500' : ''}`}
                            >
                              <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${
                                isCurrentStage&&!isCompleted ? 'bg-white animate-pulse' : (STATUS_COLORS_DOT[status]||'bg-gray-300')
                              }`} />
                              <p className={`text-[11px] font-semibold leading-tight break-words hyphens-auto ${isCurrentStage&&!isCompleted?'text-white':'text-gray-700'}`}>
                                {chain.dept_name}
                              </p>
                              {task && <p className={`text-[10px] font-bold mt-0.5 ${isCurrentStage&&!isCompleted?'text-white/80':'text-gray-500'}`}>{pct}%</p>}
                              <span className={`text-[9px] font-semibold mt-0.5 inline-block ${
                                isCurrentStage&&!isCompleted?'text-white/70':isCompleted?'text-green-700':isActive?'text-amber-700':status==='pending'?'text-blue-600':'text-gray-400'
                              }`}>{STATUS_LABELS[status]}</span>
                              {hasImages && (
                                <div className={`mt-1 flex items-center justify-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded-md ${
                                  isCurrentStage&&!isCompleted?'bg-white/20 text-white':'bg-indigo-100 text-indigo-600'
                                }`}>
                                  <Camera className="w-2.5 h-2.5" />{stageImages.length}
                                </div>
                              )}
                            </button>
                            {/* Outsource badge — separate clickable below card */}
                            {hasOutsource && (
                              <button
                                onClick={() => setOutsourcePopup(stageOutsourceJobs)}
                                className={`mt-1 w-[76px] flex items-center justify-center gap-0.5 text-[9px] font-bold px-1.5 py-1 rounded-lg transition-colors ${
                                  outsourceOverdue
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : outsourceAllDone
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                }`}
                                title="View outsource details"
                              >
                                <Truck className="w-2.5 h-2.5" />
                                {outsourceOverdue ? 'Overdue ⚠' : outsourceAllDone ? 'Received ✓' : 'Outsourced'}
                              </button>
                            )}
                            <p className={`text-[10px] mt-1 ${isCurrentStage&&!isCompleted?'text-slate-600 font-bold':'text-gray-400'}`}>#{chain.stage_order}</p>
                          </div>
                          {idx < stages.length-1 && (
                            <div className={`text-base flex-shrink-0 pb-4 ${isCompleted?'text-green-400':'text-gray-200'}`}>→</div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {activeStageKey && (() => {
                  const expandedStage = stages.find(({ chain }) =>
                    `${item.id}_${chain.stage_order}_${chain.department_id}` === activeStageKey
                  );
                  const imgs = imageMap[activeStageKey] || [];
                  if (!expandedStage || imgs.length === 0) return null;
                  return (
                    <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                          <Camera className="w-3.5 h-3.5" />
                          Stage {expandedStage.chain.stage_order}: {expandedStage.chain.dept_name} — {imgs.length} photo{imgs.length>1?'s':''}
                        </p>
                        <button onClick={() => setActiveStageKey(null)} className="text-indigo-400 hover:text-indigo-700">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {imgs.map((img, i) => (
                          <button key={img.id||i} onClick={() => setLightbox({ images: imgs, idx: i })}
                            className="relative w-16 h-16 rounded-xl overflow-hidden border-2 border-white shadow-md hover:shadow-lg group flex-shrink-0 hover:scale-105 transition-all">
                            <img src={img.image_url} alt={img.caption||'Photo'} className="w-full h-full object-cover"
                              onError={e => { e.target.parentElement.style.display='none'; }} />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────────── */
export default function ProductTrackingPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('product_tracking');
  const [mode, setMode]             = useState('proto'); // 'proto' | 'project'
  const [protoSearch, setProtoSearch] = useState('');
  const [protoInput, setProtoInput]   = useState('');
  // ── All projects list ──────────────────────────────────────────────────────
  const [projects, setProjects]           = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectTab, setProjectTab]       = useState('active');
  const [filterClient, setFilterClient]   = useState(''); // client filter
  const [clients, setClients]             = useState([]);  // unique client list
  // ── Project detail (client-style view) ─────────────────────────────────────
  const [selectedProject, setSelectedProject] = useState(null); // full project object
  const [projectDetail, setProjectDetail] = useState(null);     // { project, items, tasks, chains, imageMap }
  const [loadingDetail, setLoadingDetail] = useState(false);
  // ── Proto mode ──────────────────────────────────────────────────────────────
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const searchTimer           = useRef(null);
  const printRef              = useRef();

  // Load ONLY the selected tab's projects (active by default). Re-fetches when the
  // status dropdown changes, so completed/all are pulled on demand instead of all
  // statuses loading up-front. `lite=1` returns a slim, fast payload for the list.
  useEffect(() => {
    const controller = new AbortController();
    setLoadingProjects(true);
    api.get('/projects', {
      params: { status: projectTab, lite: 1 },   // projectTab: 'active' | 'completed' | 'all'
      signal: controller.signal,
    })
      .then(r => {
        const all = r.data.filter(p => p.status !== 'deleted');
        setProjects(all);
        // Build unique client list with counts from the currently-loaded set
        const clientMap = {};
        all.forEach(p => {
          if (p.client_name) {
            const name = p.client_name.trim();
            clientMap[name] = (clientMap[name] || 0) + 1;
          }
        });
        setClients(Object.entries(clientMap).sort((a, b) => a[0].localeCompare(b[0])));
      })
      .catch(err => {
        // Ignore aborts from rapid tab switching; surface only real failures
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        toast.error('Failed to load projects');
      })
      .finally(() => setLoadingProjects(false));
    return () => controller.abort();
  }, [projectTab]);

  // Filtered project list (status already filtered server-side via projectTab).
  // Only client + search are applied here on the already-small loaded set.
  const filteredProjects = projects.filter(p => {
    const matchClient = !filterClient || (p.client_name || '').trim().toLowerCase() === filterClient.toLowerCase();
    const q = projectSearch.toLowerCase();
    const matchSearch = !q ||
      p.name.toLowerCase().includes(q) ||
      (p.project_id||'').toLowerCase().includes(q) ||
      (p.client_name||'').toLowerCase().includes(q);
    return matchClient && matchSearch;
  });

  // Load project detail when one is selected (client-style)
  const handleSelectProject = async (project) => {
    setSelectedProject(project);
    setLoadingDetail(true);
    setProjectDetail(null);
    try {
      const [detailRes, imgRes, outsourceRes] = await Promise.all([
        api.get(`/projects/${project.id}`),
        api.get(`/projects/${project.id}/images-by-stage`),
        api.get('/outsource-jobs', { params: { project_id: project.id } }).catch(() => ({ data: [] })),
      ]);
      const { chains } = detailRes.data;
      const imageMap = {};
      imgRes.data.forEach(itemGroup => {
        itemGroup.stages.forEach(stage => {
          const chain = chains.find(c =>
            c.project_item_id === itemGroup.item_id &&
            c.stage_order === stage.stage_order
          );
          if (chain && stage.images?.length > 0) {
            const key = `${itemGroup.item_id}_${stage.stage_order}_${chain.department_id}`;
            imageMap[key] = stage.images;
          }
        });
      });
      setProjectDetail({ ...detailRes.data, imageMap, outsourceJobs: outsourceRes.data || [] });
    } catch {
      toast.error('Failed to load project details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleBackToList = () => {
    setSelectedProject(null);
    setProjectDetail(null);
  };

  // Proto code search — debounced
  useEffect(() => {
    return; // proto mode removed
    clearTimeout(searchTimer.current);
    if (!protoSearch.trim()) { setData(null); return; }
    setLoading(true);
    searchTimer.current = setTimeout(() => {
      api.get('/reports/product-tracking', { params: { proto_code: protoSearch.trim() } })
        .then(r => setData(r.data))
        .catch(() => toast.error('Search failed'))
        .finally(() => setLoading(false));
    }, 400);
  }, [protoSearch, mode]);

  // Build PDF from projectDetail (project-mode stage tracker)
  const handlePrintPDF = () => {
    if (!projectDetail) return;
    const { project, items, tasks, chains } = projectDetail;
    const title     = project?.name || selectedProject?.name || 'Project';
    const projCode  = project?.project_id || '';
    const client    = project?.client_name || '';
    const deadline  = project?.deadline ? safeDate(project.deadline) : '—';
    const printDate = new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });

    const { totalStages: totalTasks, completedStages: doneTasks, overallPct } = computeChainProgress(items, chains, tasks);

    const itemsHTML = items.map(item => {
      // Replicate ItemStageTracker logic
      const itemChains = chains
        .filter(c => c.project_item_id === item.id)
        .sort((a, b) => a.stage_order - b.stage_order);
      const itemTasks = tasks
        .filter(t => t.project_item_id === item.id)
        .sort((a, b) => (a.stage_order||0) - (b.stage_order||0));

      const stages = itemChains.length > 0
        ? itemChains.map(chain => {
            const task = itemTasks.find(t =>
              Number(t.stage_order) === Number(chain.stage_order) &&
              Number(t.department_id) === Number(chain.department_id)
            );
            return {
              dept_name:   chain.dept_name || chain.department_name || '—',
              stage_order: chain.stage_order,
              status:      task?.status || 'not_assigned',
              pct:         task && task.quantity_assigned > 0
                ? Math.round((task.quantity_completed / task.quantity_assigned) * 100) : 0,
            };
          })
        : itemTasks.map((task, i) => ({
            dept_name:   task.department_name || task.dept_name || '—',
            stage_order: task.stage_order || i + 1,
            status:      task.status || 'not_assigned',
            pct:         task.quantity_assigned > 0
              ? Math.round((task.quantity_completed / task.quantity_assigned) * 100) : 0,
          }));

      const doneStages  = stages.filter(s => s.status === 'completed').length;
      const totalStages = stages.length;
      const itemPct     = totalStages > 0 ? Math.round((doneStages / totalStages) * 100) : 0;
      const barColor    = itemPct === 100 ? '#10b981' : itemPct > 50 ? '#f59e0b' : '#3b82f6';

      const stagesHTML = stages.map((stage, idx) => {
        const dot    = STATUS_HEX[stage.status]  || '#d1d5db';
        const border = STATUS_BORDER[stage.status]|| '#e5e7eb';
        const bg     = STATUS_BG_PDF[stage.status]|| '#f9fafb';
        const lc     = stage.status==='completed'?'#15803d':stage.status==='in_progress'?'#b45309':stage.status==='pending'?'#1d4ed8':'#6b7280';
        const arrow  = idx < stages.length - 1
          ? `<div style="color:${stage.status==='completed'?'#4ade80':'#d1d5db'};font-size:16px;margin:0 2px;padding-bottom:20px">→</div>` : '';
        return `<div style="display:flex;align-items:center">
          <div style="display:flex;flex-direction:column;align-items:center;width:80px">
            <div style="width:68px;border-radius:10px;padding:6px 4px;text-align:center;border:2px solid ${border};background:${bg}">
              <div style="width:10px;height:10px;border-radius:50%;background:${dot};margin:0 auto 3px"></div>
              <p style="font-size:9px;font-weight:600;color:#374151;line-height:1.2;margin:0">${stage.dept_name}</p>
              <p style="font-size:9px;color:#6b7280;margin:1px 0">${stage.pct}%</p>
              <span style="font-size:8px;color:${lc};font-weight:500">${STATUS_LABELS[stage.status]||stage.status}</span>
            </div>
            <p style="font-size:9px;color:#9ca3af;margin:3px 0 0">#${stage.stage_order}</p>
          </div>${arrow}</div>`;
      }).join('');

      return `<div style="background:#fff;border-radius:12px;border:1px solid #f3f4f6;margin-bottom:12px;page-break-inside:avoid">
        <div style="padding:12px 14px;border-bottom:1px solid #f9fafb;display:flex;justify-content:space-between;align-items:flex-start">
          <div>
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
              ${item.proto_code ? `<span style="font-size:11px;font-family:monospace;background:#0f172a;color:#fff;padding:3px 8px;border-radius:6px;font-weight:700">${item.proto_code}</span>` : ''}
              <p style="font-size:14px;font-weight:700;color:#111827;margin:0">${item.item_name}</p>
              <span style="font-size:10px;color:#9ca3af">× ${item.quantity || 1}</span>
            </div>
            <p style="font-size:10px;color:#9ca3af;margin:3px 0 0">${doneStages}/${totalStages} stages done</p>
          </div>
          <div style="text-align:right">
            <p style="font-size:22px;font-weight:700;color:${barColor};margin:0">${itemPct}%</p>
            <p style="font-size:10px;color:#9ca3af;margin:0">Complete</p>
          </div>
        </div>
        <div style="padding:6px 14px 4px">
          <div style="background:#f3f4f6;border-radius:9999px;height:6px">
            <div style="height:6px;border-radius:9999px;background:${barColor};width:${itemPct}%"></div>
          </div>
        </div>
        <div style="padding:10px 14px;overflow-x:auto">
          <div style="display:flex;align-items:center;flex-wrap:wrap;gap:2px">${stagesHTML}</div>
        </div>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Stage Tracking — ${title}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f9fafb;color:#111827;padding:20px}
        @media print{body{padding:0;background:#fff}@page{margin:10mm 8mm;size:A4 landscape}}
      </style>
    </head><body><div style="max-width:1100px;margin:0 auto">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #f3f4f6">
        <div>
          <h1 style="font-size:20px;font-weight:700">Stage Tracking Report</h1>
          <p style="font-size:13px;color:#374151;margin-top:2px;font-weight:600">${title} <span style="font-size:11px;font-weight:normal;color:#9ca3af">(${projCode})</span></p>
          ${client ? `<p style="font-size:11px;color:#6b7280;margin-top:1px">Client: ${client}</p>` : ''}
          <p style="font-size:11px;color:#6b7280;margin-top:1px">Deadline: ${deadline}</p>
        </div>
        <div style="text-align:right">
          <p style="font-size:10px;color:#9ca3af">Printed: ${printDate}</p>
          <p style="font-size:11px;color:#374151;font-weight:600;margin-top:4px">Overall: ${overallPct}% (${doneTasks}/${totalTasks} stages)</p>
          <p style="font-size:10px;color:#9ca3af">${items.length} item${items.length!==1?'s':''}</p>
        </div>
      </div>
      ${itemsHTML}
    </div></body></html>`;

    const win = window.open('', '_blank', 'width=1200,height=800');
    if (!win) { alert('Please allow popups for PDF export'); return; }
    win.document.write(html);
    win.document.close();
    win.onload = () => setTimeout(() => win.print(), 400);
  };

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stage Tracker</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Click a project to view its items and stage-wise progress
          </p>
        </div>
        {selectedProject && projectDetail && projectDetail.items?.length > 0 && (
          <button onClick={handlePrintPDF}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
            </svg>
            Export PDF
          </button>
        )}
      </div>

      {/* Project mode — EXACTLY like client stage tracker */}
      {!selectedProject && (
        <div className="space-y-4 max-w-3xl">

          {/* Client Filter Chips — like Projects page */}
          {clients.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterClient('')}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors border ${
                  !filterClient
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
                }`}
              >
                All Clients&nbsp;
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${!filterClient ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {projects.filter(p => p.status !== 'deleted').length}
                </span>
              </button>
              {clients.map(([name, count]) => {
                const isActive = filterClient.toLowerCase() === name.toLowerCase();
                return (
                  <button
                    key={name}
                    onClick={() => setFilterClient(isActive ? '' : name)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors border ${
                      isActive
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? 'bg-white' : 'bg-amber-400'}`} />
                    {name}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={projectSearch} onChange={e => setProjectSearch(e.target.value)}
                placeholder="Search by project name, ID or client..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
            </div>
            <select value={projectTab} onChange={e => { setProjectTab(e.target.value); setProjectSearch(''); }}
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white min-w-[140px]">
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="all">All Projects</option>
            </select>
          </div>

          {loadingProjects ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-14 text-center">
              <p className="text-gray-400">No projects found</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-400">{filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''} found</p>
              <div className="space-y-3">
                {filteredProjects.map(p => {
                  const isDelayed = p.status === 'active' && p.deadline && isPast(parseISO(p.deadline));
                  const stCfg = { active:{bg:'bg-emerald-100',text:'text-emerald-700',dot:'bg-emerald-500',label:'Active'}, completed:{bg:'bg-blue-100',text:'text-blue-700',dot:'bg-blue-500',label:'Completed'}, on_hold:{bg:'bg-amber-100',text:'text-amber-700',dot:'bg-amber-500',label:'On Hold'} }[p.status] || {bg:'bg-gray-100',text:'text-gray-600',dot:'bg-gray-400',label:p.status};
                  return (
                    <div key={p.id} onClick={() => handleSelectProject(p)}
                      className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-amber-300 cursor-pointer transition-all duration-200 p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-base leading-tight">{p.name}</h3>
                            {p.project_id && (
                              <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">{p.project_id}</span>
                            )}
                          </div>
                          {p.client_name && <p className="text-xs text-gray-500 mt-1">👤 {p.client_name}</p>}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${stCfg.bg} ${stCfg.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${stCfg.dot}`} />{stCfg.label}
                            </span>
                            {isDelayed && (
                              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-600 font-medium">
                                <AlertTriangle className="w-3 h-3" /> Overdue
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 w-9 h-9 bg-amber-50 rounded-xl border border-amber-200 flex items-center justify-center">
                          <ArrowRight className="w-4 h-4 text-amber-600" />
                        </div>
                      </div>
                      {p.deadline && (
                        <>
                          <div className="border-t border-gray-100 my-3.5" />
                          <div className="flex items-center gap-5 flex-wrap text-xs text-gray-500">
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              Deadline:&nbsp;<strong className={isDelayed ? 'text-red-600' : 'text-gray-700'}>{safeDate(p.deadline)}</strong>
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Project detail — client-exact stage tracker view */}
      {selectedProject && (
        <div className="space-y-5 max-w-4xl">
          <button onClick={handleBackToList}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Projects
          </button>

          {loadingDetail ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
            </div>
          ) : projectDetail ? (() => {
            const { project, items, tasks, chains, imageMap } = projectDetail;
            const { totalStages: totalTasks, completedStages: completedTasks, overallPct } = computeChainProgress(items, chains, tasks);
            const isDelayed      = project.status === 'active' && project.deadline && isPast(parseISO(project.deadline));
            const pstMap = { active:{label:'Active',color:'bg-emerald-100 text-emerald-700'}, completed:{label:'Completed',color:'bg-blue-100 text-blue-700'}, on_hold:{label:'On Hold',color:'bg-amber-100 text-amber-700'} };
            const pst = pstMap[project.status] || { label: project.status, color:'bg-gray-100 text-gray-700' };
            return (
              <>
                {/* Project Header — same dark card as client */}
                <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl p-6 text-white shadow-lg">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h1 className="text-xl font-bold leading-tight">{project.name}</h1>
                        {project.project_id && (
                          <span className="text-xs font-mono bg-white/20 px-2 py-0.5 rounded">{project.project_id}</span>
                        )}
                      </div>
                      {project.client_name && <p className="text-slate-400 text-xs mb-2">👤 {project.client_name}</p>}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${pst.color}`}>{pst.label}</span>
                        {isDelayed && <span className="text-xs px-2.5 py-1 rounded-full bg-red-400/30 text-red-200 border border-red-400/40 font-medium">⚠ Overdue</span>}
                      </div>
                    </div>
                    <div className="text-center flex-shrink-0">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                          <circle cx="32" cy="32" r="28" stroke="rgba(255,255,255,0.15)" strokeWidth="6" fill="none" />
                          <circle cx="32" cy="32" r="28" stroke={overallPct===100?'#34d399':'#fbbf24'} strokeWidth="6" fill="none"
                            strokeDasharray={`${2*Math.PI*28}`} strokeDashoffset={`${2*Math.PI*28*(1-overallPct/100)}`}
                            strokeLinecap="round" className="transition-all duration-700" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold">{overallPct}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Progress</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-white/15">
                    <div><p className="text-xs text-slate-400 mb-0.5">Order Date</p><p className="text-sm font-semibold">{safeDate(project.order_date)}</p></div>
                    <div><p className="text-xs text-slate-400 mb-0.5">Deadline</p><p className={`text-sm font-semibold ${isDelayed?'text-red-300':''}`}>{safeDate(project.deadline)}</p></div>
                    <div><p className="text-xs text-slate-400 mb-0.5">Total Items</p><p className="text-sm font-semibold">{items.length}</p></div>
                    <div><p className="text-xs text-slate-400 mb-0.5">Stages Done</p><p className="text-sm font-semibold">{completedTasks}/{totalTasks}</p></div>
                  </div>
                </div>

                {/* Items & Stage Tracker */}
                <div className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-amber-600" />
                  <h2 className="font-bold text-gray-900">Items &amp; Stage Tracker</h2>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{items.length} item{items.length!==1?'s':''}</span>
                  <span className="text-xs text-gray-400 ml-1">— click a stage to view photos</span>
                </div>

                {items.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                    <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-400">No items in this project yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {items.map(item => (
                      <ItemStageTracker key={item.id} item={item} chains={chains} tasks={tasks} imageMap={imageMap||{}} outsourceJobs={projectDetail.outsourceJobs || []} />
                    ))}
                  </div>
                )}

                {project.description && (
                  <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                    <p className="text-xs font-semibold text-amber-700 mb-1">Project Notes</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{project.description}</p>
                  </div>
                )}
              </>
            );
          })() : null}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-10 flex flex-col items-center justify-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-full border-4 border-amber-100" />
            <div className="absolute inset-0 w-14 h-14 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-gray-800">
              {'Loading project data...'}
            </p>
            {selectedProject && (
              <p className="text-sm text-gray-400 mt-1">
                {selectedProject?.name || ''}
              </p>
            )}
            <p className="text-xs text-amber-500 mt-2 animate-pulse">Fetching stage tracking report</p>
          </div>
        </div>
      )}

      {/* Results */}
      {data && !loading && (
        <>
          {/* Result header */}
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-gray-700">
              {data.items.length} item{data.items.length !== 1 ? 's' : ''} found
            </span>
            {false && (
              <span className="text-xs text-gray-400">for proto code "<span className="font-mono text-gray-600">{protoSearch}</span>"</span>
            )}
          </div>

          {data.items.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">
                {'No products found in this project'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.items.map(item => (
                <ProtoItemCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  
    </PermissionGuard>
  );
}
